<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && page.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card h-100">
                    <div class="card-body">
                        <add-edit-form
                            v-model="page"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-title-change="onTitleChange"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div class="mb-3">
                            <a
                                v-if="editMode"
                                :href="previewUrl"
                                class="btn btn-primary btn-static waves-effect waves-light m-1"
                                target="_blank"
                                @click="onPreviewButtonClick"
                            >
                                <i class="far fa-eye" />
                                Preview
                            </a>
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="page"
                            :slug="page.slug"
                            :slug-error="slugError"
                            @meta-change="onMetaChange"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import { frontendUrl } from '@/config';
import AddEditForm from '@/components/pages/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Simple Page'
    },

    validations: {
        page: {
            title: {
                required
            },
            slug: {
                required
            },
            content: {
                atLeastOneItem: val => val && val.length > 0
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit page' : 'Add page',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Pages',
                    to: '/pages'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            page: {
                slug: '',
                content: [],
                meta: {}
            },
            informationKey: 0,
            serverErrors: [],
            pageValueStringify: ''
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                meta: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        previewUrl() {
            return `${frontendUrl}/${this.page.slug}`;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.page,
                this.serverErrors,
                'slug'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.page = await this.getPage(this.id);
                this.pageValueStringify = JSON.stringify(this.page);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addPage: 'pages/store',
            deletePage: 'pages/destroy',
            getPage: 'pages/show',
            updatePage: 'pages/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addPage(this.page);

                    this.$router.push(`/pages/${added.id}/edit`);
                } else {
                    const page = await this.updatePage({
                        id: this.id,
                        data: this.page
                    });

                    this.page.updatedAt = page.updatedAt;
                    this.pageValueStringify = JSON.stringify(this.page);

                    this.$toaster('Page updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deletePage(this.id);

                this.$router.push('/pages');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onPreviewButtonClick(e) {
            if (this.pageValueStringify !== JSON.stringify(this.page)) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onMetaChange(meta) {
            this.page.meta = { ...meta };
        },

        onTitleChange(title) {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(title);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.page.slug = slug;
        }
    }
};
</script>
